/* The create-API-key flow, shared by the settings page (ApiKeysPage) and the
 * quick-create modal reachable from the docs. Two phases in one Dialog: a
 * name/expiry/workspace form, then a one-time reveal of the new key value with
 * a copy button — the value is never shown in full again. */

import { useState } from "react";
import { Select, TextField } from "../../design-system";
import { friendlyError } from "../../analytics";
import { trackApiKeyCreated } from "../../analytics/tracking";
import {
  useAction,
  useIdentityClient,
  type ApiKey,
  type Workspace,
} from "../../identity";
import { CopyButton } from "../../components/controls/CopyButton";
import { KeysModal, ModalButton } from "./KeysModal";

/** The one-time reveal of a freshly created or rotated key value. */
export function RevealValue({ value }: { value: string }) {
  return (
    <div className="flex flex-col gap-2">
      <div
        className="flex items-center gap-1 rounded-[5px] px-3 py-2"
        style={{
          background: "var(--surface-2)",
          border: "1px solid var(--border)",
        }}
      >
        <code className="tw-mono min-w-0 flex-1 break-all text-[12px] text-[var(--text)]">
          {value}
        </code>
        <CopyButton value={value} label="Copy key value" />
      </div>
      <p className="text-[12px] text-[var(--text-muted)]">
        Treat this key like a password — it won’t be shown in full again.
      </p>
    </div>
  );
}

export function CreateKeyDialog({
  open,
  workspaces,
  initialWorkspaceId,
  onClose,
  onCreated,
}: {
  open: boolean;
  workspaces: Workspace[];
  initialWorkspaceId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const client = useIdentityClient();
  const [name, setName] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  // null = untouched; the opener's workspace applies until the user picks.
  const [chosen, setChosen] = useState<string | null>(null);
  const [created, setCreated] = useState<ApiKey | null>(null);
  const workspaceId = chosen ?? initialWorkspaceId;
  const create = useAction(
    async (targetWorkspaceId: string, keyName: string, expiry?: string) => {
      const key = await client.createApiKey(targetWorkspaceId, keyName, expiry);
      trackApiKeyCreated(key.workspaceId);
      setCreated(key);
    },
    "createApiKey",
  );

  const closeAndReset = () => {
    setName("");
    setExpiresAt("");
    setChosen(null);
    setCreated(null);
    create.reset();
    onClose();
  };

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;

    if (await create.run(workspaceId, trimmed, expiresAt || undefined)) {
      onCreated();
    }
  };

  // Phase 2: the key exists — show its value once, no way back to the form.
  if (created) {
    return (
      <KeysModal
        open={open}
        title="Key created"
        description={`“${created.name}” is ready to use.`}
        onClose={closeAndReset}
        footer={
          <ModalButton variant="brand" onClick={closeAndReset}>
            Done
          </ModalButton>
        }
      >
        <RevealValue value={created.value} />
      </KeysModal>
    );
  }

  // Expiry must be in the future; the API takes a date, not a duration.
  const minExpiry = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split("T")[0];
  })();

  return (
    <KeysModal
      open={open}
      title="Create API key"
      description="The key authenticates gateway traffic for one workspace."
      busy={create.busy}
      onClose={closeAndReset}
      footer={
        <>
          <ModalButton disabled={create.busy} onClick={closeAndReset}>
            Cancel
          </ModalButton>
          <ModalButton
            variant="brand"
            disabled={create.busy || !name.trim()}
            onClick={submit}
          >
            {create.busy ? "Creating…" : "Create key"}
          </ModalButton>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Select
          label="Workspace"
          value={workspaceId}
          onChange={(e) => setChosen(e.target.value)}
        >
          {workspaces.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </Select>
        <TextField
          label="Name"
          placeholder="e.g. CI pipeline"
          value={name}
          autoFocus
          onChange={(e) => setName(e.target.value)}
          error={create.error ? friendlyError(create.error).message : undefined}
        />
        <TextField
          label="Expiration"
          type="date"
          min={minExpiry}
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          hint="Optional — leave empty for a key that never expires."
        />
      </form>
    </KeysModal>
  );
}
